import { inject } from '@angular/core';
import { getMessageError, isHttpStatusError } from '@mkp/shared/data-access';
import { accountMembershipActions } from '@mkp/account-membership/actions';
import {
  AccountMembershipMapper,
  AccountMembershipResource,
} from '@mkp/account-membership/data-access';
import { Account } from '@mkp/account/models';
import { authActions, authOnboardingRecoveryActions } from '@mkp/auth/actions';
import { SentryService } from '@mkp/core/util-sentry';
import { legalEntityDetailsActions } from '@mkp/legal-entity/actions';
import {
  activeUsersActions,
  editPermissionsActions,
  userAccountMembershipListActions,
} from '@mkp/user-account-membership/actions';
import { VacancyResource } from '@mkp/vacancy/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { selectLoggedInUser } from '@mkp/user/state-logged-in/selectors';
import { from, map, of } from 'rxjs';
import { catchError, exhaustMap, filter, mergeMap, reduce, switchMap } from 'rxjs/operators';

export const loadAccountMemberships = createEffect(
  (actions$ = inject(Actions), accountMembershipResource = inject(AccountMembershipResource)) => {
    return actions$.pipe(
      ofType(accountMembershipActions.loadAccountMemberships),
      switchMap(({ query }) =>
        accountMembershipResource.listAccountMemberships(query).pipe(
          switchMap((accountMemberships) =>
            of(accountMembershipActions.loadAccountMembershipsSuccess({ accountMemberships }))
          ),
          catchError((error: unknown) =>
            of(
              accountMembershipActions.loadAccountMembershipsFailure({
                error: getMessageError(error, 'loadAccountMemberships'),
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const loadMoreAccountMemberships = createEffect(
  (actions$ = inject(Actions), accountMembershipResource = inject(AccountMembershipResource)) => {
    return actions$.pipe(
      ofType(accountMembershipActions.loadMoreAccountMemberships),
      switchMap(({ query }) =>
        accountMembershipResource.listPaginatedAccountMemberships(query).pipe(
          switchMap(({ accountMemberships, filter, _links, totalCount }) =>
            of(
              accountMembershipActions.loadMoreAccountMembershipsSuccess({
                accountMemberships,
                _links,
                filter,
                totalCount,
              })
            )
          ),
          catchError((error: unknown) =>
            of(
              accountMembershipActions.loadAccountMembershipsFailure({
                error: getMessageError(error, 'loadMoreAccountMemberships'),
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const loadMembershipPerAccountCount = createEffect(
  (actions$ = inject(Actions), accountMembershipResource = inject(AccountMembershipResource)) => {
    return actions$.pipe(
      ofType(legalEntityDetailsActions.accountsLoadSuccess),
      filter(({ accounts }) => accounts.length > 0),
      switchMap(({ accounts }) =>
        from(accounts).pipe(
          mergeMap(getMembershipCount(accountMembershipResource)),
          accumulateMembershipCounts(),
          map((membershipPerAccountCount) =>
            legalEntityDetailsActions.membershipPerAccountCountLoadSuccess({
              membershipPerAccountCount,
            })
          ),
          catchError((error) =>
            of(
              legalEntityDetailsActions.membershipPerAccountCountLoadFailure({
                error: getMessageError(error, 'membershipPerAccountCountLoadFailure'),
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const createAccountMembership = createEffect(
  (actions$ = inject(Actions), accountMembershipResource = inject(AccountMembershipResource)) => {
    return actions$.pipe(
      ofType(accountMembershipActions.createAccountMembership),
      switchMap(({ accountId, userId }) =>
        accountMembershipResource.createAccountMembership({ accountId, userId }).pipe(
          switchMap((accountMembership) =>
            of(accountMembershipActions.createAccountMembershipSuccess({ accountMembership }))
          ),
          catchError((error: unknown) =>
            of(
              accountMembershipActions.createAccountMembershipFailure({
                error: getMessageError(error, 'createAccountMembership'),
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const deleteAccountMembership = createEffect(
  (
    actions$ = inject(Actions),
    accountMembershipResource = inject(AccountMembershipResource),
    vacancyResource = inject(VacancyResource)
  ) => {
    return actions$.pipe(
      ofType(
        editPermissionsActions.deleteAccountMembership,
        activeUsersActions.deleteAccountMembership,
        userAccountMembershipListActions.deleteAccountMembership
      ),
      switchMap(({ accountMembership, isSelfDelete }) =>
        vacancyResource
          .removeRecruiterFromVacancies(accountMembership.user.id, accountMembership.accountId)
          .pipe(
            switchMap(() =>
              accountMembershipResource.deleteAccountMembership(accountMembership.id)
            ),
            map(() => accountMembershipActions.deleteSuccess({ accountMembership, isSelfDelete })),
            catchError((error: unknown) => {
              const status = isHttpStatusError(error) ? error.status : 500;
              return of(
                accountMembershipActions.deleteFailure({
                  status,
                  accountMembershipId: accountMembership.id,
                  isSelfDelete,
                })
              );
            })
          )
      )
    );
  },
  { functional: true }
);

export const patchRoles = createEffect(
  (
    actions$ = inject(Actions),
    accountMembershipResource = inject(AccountMembershipResource),
    sentryService = inject(SentryService)
  ) => {
    return actions$.pipe(
      ofType(editPermissionsActions.rolesSaved),
      exhaustMap(({ roles, accountMembershipId, _version, isSelfUpdate }) =>
        accountMembershipResource.patchRoles({ roles, accountMembershipId, _version }).pipe(
          map((accountMembership) =>
            accountMembershipActions.patchRolesSuccess({ accountMembership, isSelfUpdate })
          ),
          catchError((error: unknown) => {
            const status = isHttpStatusError(error) ? error.status : 500;

            if (status === 400 || status === 500) {
              sentryService.captureException({
                name: 'CustomError',
                message: 'unknown error when removing account-membership',
                error,
              } as Error);
            }

            return of(
              accountMembershipActions.patchRolesFailure({
                status,
              })
            );
          })
        )
      )
    );
  },
  { functional: true }
);

export const initiateVerifiedUserAccountMemberships = createEffect(
  (
    actions$ = inject(Actions),
    accountMembershipResource = inject(AccountMembershipResource),
    store = inject(Store),
    mapper = inject(AccountMembershipMapper)
  ) => {
    return actions$.pipe(
      ofType(authActions.loadAccountsAfterLoginSuccess),
      concatLatestFrom(() => store.select(selectLoggedInUser).pipe(filter(Boolean))),
      switchMap(([{ accounts }, { id }]) =>
        accountMembershipResource
          .getWithQuery({
            params: getAccountMembershipsFilterForLoggedInUser(id),
            headers: { maxRetry: 4 },
          })
          .pipe(
            map(({ _embedded, totalCount }) => ({
              accountMemberships: _embedded.results.map((dto) => mapper.fromJson(dto)),
              totalCount,
            })),
            map(({ accountMemberships, totalCount }) =>
              totalCount
                ? authActions.loadActiveAccountMembershipsSuccess({
                    accountMemberships,
                    totalCount,
                  })
                : authActions.noMembershipsFoundAfterLogin({ accounts })
            ),
            catchError((error) =>
              // we need to recover from failed onboarding
              of(
                getNoMembershipsFoundAction(
                  accounts,
                  getMessageError(error, 'initiateVerifiedUserAccountMemberships'),
                  error
                )
              )
            )
          )
      )
    );
  },
  { functional: true }
);

export const handleNoMembershipsFound = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(authActions.noMembershipsFoundAfterLogin),
      map(({ accounts }) =>
        getNoMembershipsFoundAction(accounts, { error: { message: 'no-memberships' } })
      )
    ),
  { functional: true }
);

const getMembershipCount =
  (accountMembershipResource: AccountMembershipResource) =>
  ({ id: accountId }: Account) =>
    accountMembershipResource
      .getWithQuery({ params: { limit: 0 }, withAccountId: accountId })
      .pipe(map(({ totalCount }) => ({ accountId, totalCount })));

const accumulateMembershipCounts = () =>
  reduce<{ accountId: Account['id']; totalCount: number }, Record<Account['id'], number>>(
    (acc, { accountId, totalCount }) => ({ ...acc, [accountId]: totalCount }),
    {}
  );

const getErrorStatus = (error: unknown): number | undefined =>
  (error as { status: number })?.status;

const getAccountMembershipsFilterForLoggedInUser = (userId: string) => ({
  filter: `user.id==${userId}`,
  limit: 200,
});

const getNoMembershipsFoundAction = (accounts: Account[], errorMessage: any, error?: any) =>
  accounts.length === 1 && (error ? getErrorStatus(error) === 403 : true)
    ? authOnboardingRecoveryActions.onboardingFailedFromMembership({
        account: accounts[0],
      })
    : authActions.loadActiveAccountMembershipsFailed({
        error: errorMessage,
      });
